import React from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    IconButton,
    Toolbar,
    Typography,
    Tooltip,
    Skeleton,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    CropSquare as RectangleIcon,
    Pentagon as PolygonIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Undo as UndoIcon,
    Check as CompleteIcon,
    Close as CancelIcon,
    CheckCircle as DoneIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { usePermissions } from '../../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../../utils/permissions';

const RoomEditToolbar = ({
    isDrawingMode,
    drawingMode,
    isROIVisible,
    currentPolygonLength,
    onStartRectangle,
    onStartPolygon,
    onToggleVisibility,
    onUndo,
    onComplete,
    onCancel,
    onExitEditMode,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { projectId } = useParams();
    const { canProject, isLoading } = usePermissions();

    // Defense-in-depth: skeleton while loading, hide if no permission
    if (isLoading) {
        return (
            <Box
                sx={{
                    backgroundColor: 'background.paper',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 1,
                    flexShrink: 0,
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                }}
            >
                <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
            </Box>
        );
    }

    if (!canProject(projectId, PERMISSIONS.PROJECT_WRITE)) {
        return null;
    }

    return (
        <Box
            sx={{
                backgroundColor: 'background.paper',
                borderBottom: '1px solid',
                borderColor: 'divider',
                boxShadow: 1,
                flexShrink: 0,
            }}
        >
            <Toolbar
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 2,
                    minHeight: { xs: 56, sm: 64 },
                    px: { xs: 2, sm: 3 },
                }}
            >
                {/* Left Section: Drawing Tools - Always Visible */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography
                        variant="h6"
                        sx={{
                            display: { xs: 'none', md: 'block' },
                            mr: 2,
                            fontWeight: 600,
                        }}
                    >
                        Edit Rooms
                    </Typography>

                    <Tooltip title="Draw Rectangle">
                        {isMobile ? (
                            <IconButton
                                onClick={onStartRectangle}
                                color={
                                    isDrawingMode && drawingMode === 'rectangle'
                                        ? 'primary'
                                        : 'default'
                                }
                                size="large"
                                sx={{
                                    bgcolor:
                                        isDrawingMode && drawingMode === 'rectangle'
                                            ? 'primary.main'
                                            : 'transparent',
                                    color:
                                        isDrawingMode && drawingMode === 'rectangle'
                                            ? 'primary.contrastText'
                                            : 'inherit',
                                    '&:hover': {
                                        bgcolor:
                                            isDrawingMode && drawingMode === 'rectangle'
                                                ? 'primary.dark'
                                                : 'action.hover',
                                    },
                                }}
                            >
                                <RectangleIcon />
                            </IconButton>
                        ) : (
                            <Button
                                variant={
                                    isDrawingMode && drawingMode === 'rectangle'
                                        ? 'contained'
                                        : 'outlined'
                                }
                                startIcon={<RectangleIcon />}
                                onClick={onStartRectangle}
                            >
                                Rectangle
                            </Button>
                        )}
                    </Tooltip>

                    <Tooltip title="Draw Polygon">
                        {isMobile ? (
                            <IconButton
                                onClick={onStartPolygon}
                                color={
                                    isDrawingMode && drawingMode === 'polygon'
                                        ? 'primary'
                                        : 'default'
                                }
                                size="large"
                                sx={{
                                    bgcolor:
                                        isDrawingMode && drawingMode === 'polygon'
                                            ? 'primary.main'
                                            : 'transparent',
                                    color:
                                        isDrawingMode && drawingMode === 'polygon'
                                            ? 'primary.contrastText'
                                            : 'inherit',
                                    '&:hover': {
                                        bgcolor:
                                            isDrawingMode && drawingMode === 'polygon'
                                                ? 'primary.dark'
                                                : 'action.hover',
                                    },
                                }}
                            >
                                <PolygonIcon />
                            </IconButton>
                        ) : (
                            <Button
                                variant={
                                    isDrawingMode && drawingMode === 'polygon'
                                        ? 'contained'
                                        : 'outlined'
                                }
                                startIcon={<PolygonIcon />}
                                onClick={onStartPolygon}
                            >
                                Polygon
                            </Button>
                        )}
                    </Tooltip>

                    <Tooltip title={isROIVisible ? 'Hide Rooms' : 'Show Rooms'}>
                        {isMobile ? (
                            <IconButton
                                onClick={onToggleVisibility}
                                color={isROIVisible ? 'primary' : 'default'}
                                size="large"
                            >
                                {isROIVisible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                            </IconButton>
                        ) : (
                            <Button
                                variant={isROIVisible ? 'contained' : 'outlined'}
                                startIcon={
                                    isROIVisible ? <VisibilityIcon /> : <VisibilityOffIcon />
                                }
                                onClick={onToggleVisibility}
                            >
                                {isROIVisible ? 'Hide' : 'Show'}
                            </Button>
                        )}
                    </Tooltip>
                </Box>

                {/* Center Section: Tool Instructions & Controls */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        alignItems: 'center',
                        flex: 1,
                        justifyContent: 'center',
                        px: 2,
                    }}
                >
                    {isDrawingMode && (
                        <>
                            {/* Instruction Text Box - Hidden on mobile */}
                            <Box
                                sx={{
                                    display: { xs: 'none', md: 'block' },
                                    px: 2.5,
                                    py: 1,
                                    borderRadius: 1.5,
                                    bgcolor: '#F5F7FA',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                        fontWeight: 500,
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    {drawingMode === 'rectangle'
                                        ? 'Click two opposite corners'
                                        : currentPolygonLength === 0
                                          ? 'Click to add points (min 3)'
                                          : `${currentPolygonLength} ${currentPolygonLength === 1 ? 'point' : 'points'} added`}
                                </Typography>
                            </Box>

                            {/* Action Buttons - Separate and Clean */}
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                {/* Undo Button - Only for polygon with points */}
                                {drawingMode === 'polygon' && currentPolygonLength > 0 && (
                                    <Tooltip title="Undo last point">
                                        <IconButton
                                            onClick={onUndo}
                                            size="medium"
                                            sx={{
                                                bgcolor: '#FFF4E6',
                                                color: '#FF9800',
                                                border: '1px solid #FFE0B2',
                                                '&:hover': {
                                                    bgcolor: '#FFE0B2',
                                                },
                                            }}
                                        >
                                            <UndoIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {/* Complete Button - Only for polygon with 3+ points */}
                                {drawingMode === 'polygon' && currentPolygonLength >= 3 && (
                                    <Button
                                        variant="contained"
                                        color="success"
                                        size="medium"
                                        onClick={onComplete}
                                        startIcon={<CompleteIcon />}
                                        sx={{
                                            px: 2.5,
                                            py: 0.75,
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            borderRadius: 1.5,
                                            boxShadow: 'none',
                                            '&:hover': {
                                                boxShadow: '0 2px 8px rgba(46, 125, 50, 0.3)',
                                            },
                                        }}
                                    >
                                        Complete
                                    </Button>
                                )}

                                {/* Cancel Button - Subtle icon button */}
                                <Tooltip title="Cancel drawing">
                                    <IconButton
                                        onClick={onCancel}
                                        size="medium"
                                        sx={{
                                            color: 'text.secondary',
                                            '&:hover': {
                                                bgcolor: 'error.lighter',
                                                color: 'error.main',
                                            },
                                        }}
                                    >
                                        <CancelIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </>
                    )}
                </Box>

                {/* Right Section: Done Button */}
                <Box sx={{ display: 'flex' }}>
                    {isMobile ? (
                        <Tooltip title="Exit Edit Mode">
                            <IconButton
                                onClick={onExitEditMode}
                                color="success"
                                size="large"
                                sx={{
                                    backgroundColor: 'success.main',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: 'success.dark',
                                    },
                                }}
                            >
                                <DoneIcon />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<DoneIcon />}
                            onClick={onExitEditMode}
                            sx={{ fontWeight: 600, px: 3 }}
                        >
                            Done
                        </Button>
                    )}
                </Box>
            </Toolbar>
        </Box>
    );
};

RoomEditToolbar.propTypes = {
    isDrawingMode: PropTypes.bool.isRequired,
    drawingMode: PropTypes.string,
    isROIVisible: PropTypes.bool.isRequired,
    currentPolygonLength: PropTypes.number.isRequired,
    onStartRectangle: PropTypes.func.isRequired,
    onStartPolygon: PropTypes.func.isRequired,
    onToggleVisibility: PropTypes.func.isRequired,
    onUndo: PropTypes.func.isRequired,
    onComplete: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onExitEditMode: PropTypes.func.isRequired,
};

export default RoomEditToolbar;
